import { useNavigation } from "@react-navigation/native";
import React, { memo, useContext, useEffect, useState } from "react";
import { StyleSheet, TouchableNativeFeedback, View } from "react-native";
import Image from "@components/common/Image";
import { useDispatch, useSelector } from "react-redux";
import CheckGreen from "@assets/svg/CheckGreen";
import DownArrowSmall from "@assets/svg/DownArrowSmall";
import Text from "@components/AppText";
import testIds from "@constants/testIds";
import useLeaderState from "@hooks/useLeaderState";
import { actions as productActions } from "@reducers/productReducer";
import { actions } from "@reducers/qtySelectReducer";
import { CartContext } from "@screens/PlaceOrder/PlaceOrderView";
import { notifyForOOS } from "@services/api";
import debounce from "@utils/debounce";
import {
  ORDER_FLOW_EVENTS,
  SHARING_EVENTS,
} from "../../constants/analyticsEventsCT";
import parseTemplate from "../../utils/parseTemplate";
import { shareOnWhatsapp } from "../../utils/shareEntity";
import useCartActions from "../CMS/useCartActions";
import AddnMinBtn from "./AddnMinBtn";
import CommissionStrip from "./CommissionStrip";
import DiscountStrip from "./DiscountStrip";
import KhareedBtn from "./KhareedBtn";
import { normalize } from "./metrics";
import NotifyButton from "./NotifyButton";
import ShareOnWA from "./ShareOnWA";

const formatPrice = (price) => {
  price = price + "";
  return price?.split(".") || "";
};

const Product = memo(
  ({
    data,
    index,
    navigateMethod = "navigate",
    routeName = "",
    trackEvent = () => {},
    disabled = false,
    notFromHome = false,
    sharingTemplate = "",
    sharingLink = "",
    ...props
  }) => {
    const { children = [], variant_identifier } = data;

    const [firstRender, setFirstRender] = useState(true);
    const { identifier, displayId } = useSelector(
      (state) => state.qtySelection,
    );

    const [displayItem, setDisplayItem] = useState(
      children.find((el) => el.sku_id === data.sku_id),
    );

    const dispatch = useDispatch();

    const user = useSelector((state) => state.auth.user);
    const [notified, setNotified] = useState(false);

    const cartConsumer = useContext(CartContext);
    useEffect(() => {
      if (identifier === variant_identifier && displayId !== "") {
        setDisplayItemHandler(displayId);
      }
    });

    const setDisplayItemHandler = (id) => {
      const newItem = children.find((el) => el.sku_id === id);
      setDisplayItem(newItem);
    };
    const { selectedContact, cart } = useSelector((state) => ({
      selectedContact: state.customer.selectedContact,
    }));

    useEffect(() => {
      setFirstRender(false);
    }, []);

    useEffect(() => {
      if (
        typeof cartConsumer !== "undefined" &&
        typeof cartConsumer.updateCart === "function" &&
        firstRender === false
      ) {
        cartConsumer.updateCart();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemCount]);

    const {
      discount = "",
      discount_type = "",
      short_name_hi,
      product_image,
      price_tl_single_buy,
      price_per_unit,
      mrp,
      grammage,
      custom_text,
      custom_symbol,
      product_status,
      product_description_hi,
      product_name_hi,
      images = [],
      sku_id,
      slug,
      onPlusClick,
      onMinusClick,
      quantity,
      price_tl_commission,
    } = displayItem || data;

    // const dataObj = { ...props, ...data };
    const discount_price = mrp - price_tl_single_buy;
    const dataObj = {
      mrp,
      price_tl_single_buy,
      product_name_hi,
      discount_price,
      leaderName: props.leaderName,
      leaderId: user.user_id,
      slug,
    };
    const [showCartActions, setShowCartActions] = useState(false);
    const [count, setCount] = useState(null);

    const { canLeaderUseApp } = useLeaderState();
    const shareOnWhatsappRestricted = (props) => {
      if (!canLeaderUseApp()) {
        return;
      }
      trackEvent(SHARING_EVENTS.PRODUCT_SHARE, {
        sku_id,
        price_tl_single_buy,
        product_name: product_name_hi,
        position: index,
      });
      shareOnWhatsapp(props);
    };

    const { addCart, removeCart, isCartValueLoading, countOfItem } =
      useCartActions();

    let itemCount = quantity || countOfItem(sku_id);
    const navigation = useNavigation();
    let finalProductCount;

    if (itemCount) {
      if (count) {
        finalProductCount = itemCount + count;
      } else {
        finalProductCount = itemCount;
      }
    } else {
      if (count) {
        finalProductCount = count;
      }
    }

    const getItemCount = (count) => {
      let finalProductCount;
      if (itemCount) {
        if (count) {
          finalProductCount = itemCount + count;
        } else {
          finalProductCount = itemCount;
        }
      } else {
        if (count) {
          finalProductCount = count;
        }
      }
      return finalProductCount;
    };

    const debounceCb = (sku_id, user_id, cb, count, eventPayload) => {
      addCart(sku_id, user_id, cb, count, eventPayload);
    };

    const debounceRemoveCartCb = (sku_id, user_id, cb, count, eventPayload) => {
      removeCart(sku_id, user_id, cb, count, eventPayload);
    };

    const debounceAddToCart = React.useCallback(debounce(debounceCb, 700), []);
    const debounceRemoveFromCart = React.useCallback(
      debounce(debounceRemoveCartCb, 700),
      [],
    );

    const onPressPlus = () => {
      if (!canLeaderUseApp()) {
        return;
      }
      const updatedCount = count === null ? 1 : count + 1;
      setCount(updatedCount);
      const eventPayload = {
        sku_id,
        price_tl_single_buy,
        product_name_hi,
        position: index,
      };
      debounceAddToCart(
        sku_id,
        selectedContact.user_id,
        () => {
          setCount(null);
        },
        getItemCount(updatedCount),
        eventPayload,
      );
    };

    const onPressMinus = () => {
      if (!canLeaderUseApp()) {
        return;
      }
      const updatedCount = count === null ? -1 : count - 1;
      if (finalProductCount > 0) {
        setCount(updatedCount);
      }

      const eventPayload = {
        sku_id,
        price_tl_single_buy,
        product_name_hi,
        position: index,
      };
      debounceRemoveFromCart(
        sku_id,
        selectedContact.user_id,
        () => {
          setCount(null);
        },
        getItemCount(updatedCount),
        eventPayload,
      );
    };

    useEffect(() => {
      if (finalProductCount > 0) {
        setShowCartActions(true);
      } else {
        setShowCartActions(false);
      }
    }, [finalProductCount]);

    const shareProductOnWhatsApp = () => {
      const { app_share_app_link } = user;

      let percentage =
        ((parseInt(mrp) - parseInt(price_tl_single_buy)) / parseInt(mrp)) * 100;

      if (percentage === 0) {
        percentage = "";
      } else {
        percentage = percentage.toFixed(0);
      }

      const link = `${app_share_app_link}?app_screen=ProductDetails&slug=${slug}&channel=cl_product_share`;

      const message = `
    ${product_name_hi}\nCityMall Price - ${price_tl_single_buy} ~MRP~ - ~${mrp}~ ${
      percentage == 0 ? "" : `(${percentage}%) Off`
    }\n\nआर्डर करे: ${link}
    `;
      shareOnWhatsappRestricted({ message, image: product_image });
    };

    /* out of stock handling here */

    const notifyOOS = () => {
      notifyForOOS({ sku_id })
        .then(() => {
          dispatch(productActions.requestForOosSkus());
          setNotified(true);
        })
        .catch((e) => {
          console.log(e);
        });
    };

    const oosProducts =
      useSelector((state) => state.products.oosProducts) || [];

    const isOutOfStockNotified =
      oosProducts.findIndex((p) => p.sku_id === sku_id) !== -1;

    useEffect(() => {
      setNotified(isOutOfStockNotified);
    }, [isOutOfStockNotified, oosProducts]);

    return (
      <TouchableNativeFeedback
        testID={`${testIds.Product_Details_Infinite_List}${index}`}
        disabled={disabled}
        onPress={() => {
          navigation[navigateMethod]("ProductDetails", {
            slug,
            fromScreen: routeName,
          });
          trackEvent(ORDER_FLOW_EVENTS.PRODUCT_CLICKED, {
            sku_id,
            product_price: price_tl_single_buy,
            product_name: product_name_hi,
            position: index,
          });
        }}
      >
        <View style={styles.container}>
          <DiscountStrip discount_type={discount_type} discount={discount} />
          <View style={{ flexDirection: "row" }}>
            <View>
              <Image
                style={{
                  width: normalize(100),
                  height: normalize(100),
                  opacity: product_status == "Active" ? 1 : 0.2,
                }}
                source={
                  product_image
                    ? {
                        uri: product_image,
                      }
                    : require("@assets/images/defaultImage.webp")
                }
                resizeMode={Image.resizeMode.contain}
              />
            </View>
            <View
              style={{
                flex: 1,
                paddingLeft: normalize(8),
              }}
            >
              <Text
                style={styles.productTitle}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {product_name_hi}
              </Text>
              {product_status === "Active" ? (
                <View
                  style={{
                    marginBottom: normalize(10),
                    flexDirection: "row",
                    flexWrap: "wrap",
                  }}
                >
                  <View
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Text bold style={{ fontSize: normalize(18) }}>
                      ₹{formatPrice(price_tl_single_buy)[0]}
                      {formatPrice(price_tl_single_buy)[1] && (
                        <Text bold style={{ fontSize: normalize(12) }}>
                          .{formatPrice(price_tl_single_buy)[1]}
                        </Text>
                      )}
                    </Text>
                  </View>
                  <View
                    style={{
                      marginHorizontal: normalize(14),
                      // justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      style={{
                        ...styles.mrp,
                        fontSize: normalize(15),
                        marginTop: normalize(7),
                        textDecorationLine:
                          parseFloat(mrp) !== parseFloat(price_tl_single_buy)
                            ? "line-through"
                            : "none",
                      }}
                    >
                      ₹{mrp}
                    </Text>
                  </View>

                  {price_per_unit ? (
                    <View
                      style={{
                        // justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Text
                        numberOfLines={2}
                        style={[
                          styles.price_per_unit,

                          { fontSize: normalize(13), marginTop: normalize(5) },
                        ]}
                      >
                        ({price_per_unit})
                      </Text>
                    </View>
                  ) : (
                    <></>
                  )}
                </View>
              ) : (
                <Text
                  style={{
                    fontSize: normalize(16),
                    color: "#FF2E00",
                    marginBottom: 9,
                  }}
                >
                  यह प्रोडक्ट स्टॉक में नहीं है
                </Text>
              )}
              {product_status === "Active" ? (
                <>
                  <View style={{ alignItems: "flex-start" }}>
                    <CommissionStrip commission={price_tl_commission} />
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      paddingTop: 15,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        flex: 1,
                      }}
                    >
                      <TouchableNativeFeedback
                        disabled={children.length <= 1}
                        onPress={() => {
                          trackEvent(ORDER_FLOW_EVENTS.PRODUCT_BUNDLED, {
                            sku_id,
                            price_tl_single_buy,
                            product_name_hi,
                            position: index,
                          });
                          dispatch(
                            actions.showBottomSheet({
                              items: children,
                              identifier: variant_identifier,
                            }),
                          );
                        }}
                      >
                        <View
                          style={{
                            // width: normalize(90),
                            width: normalize(100),
                            backgroundColor:
                              children.length <= 1
                                ? "rgba(0, 0, 0, 0.1)"
                                : "white",
                            paddingHorizontal: normalize(10),
                            borderRadius: 6,
                            height: normalize(30),
                            borderWidth: children.length <= 1 ? 0 : 1,
                            borderColor: "rgba(0, 0, 0, 0.2)",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Text style={{ fontSize: normalize(14) }}>
                            {grammage}
                          </Text>
                          {children.length <= 1 ? null : <DownArrowSmall />}
                        </View>
                      </TouchableNativeFeedback>
                      <ShareOnWA
                        width={normalize(100)}
                        onPress={() => {
                          if (!canLeaderUseApp()) {
                            return;
                          }
                          if (notFromHome) {
                            if (sharingTemplate) {
                              const message = parseTemplate(
                                sharingTemplate,
                                dataObj,
                                "_",
                              );
                              shareOnWhatsappRestricted({
                                message,
                                image: product_image,
                              });
                            } else {
                              shareProductOnWhatsApp();
                            }
                          } else {
                            if (sharingTemplate) {
                              const message = parseTemplate(
                                sharingTemplate,
                                dataObj,
                                "_",
                              );
                              shareOnWhatsappRestricted({
                                message: `${message}\n${sharingLink}`,
                                image: product_image,
                              });
                            } else {
                              shareProductOnWhatsApp();
                            }
                          }
                        }}
                      />
                    </View>
                  </View>
                </>
              ) : !notified ? (
                <View style={{}}>
                  <NotifyButton onPress={notifyOOS} width="75%" />
                </View>
              ) : (
                <View
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    borderRadius: 6,
                    paddingHorizontal: 5,
                    // paddingVertical: normalize(7),
                    flexDirection: "row",
                    alignItems: "center",
                    width: normalize(170),
                    height: normalize(30),
                  }}
                >
                  <CheckGreen />
                  <Text
                    style={{
                      color: "rgba(0, 0, 0, 0.56)",
                      fontSize: normalize(14),
                      marginLeft: normalize(2),
                    }}
                  >
                    आपको सूचित किया जायेगा
                  </Text>
                </View>
              )}
            </View>
          </View>

          {custom_symbol && custom_text ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                // marginTop: normalize(10),
                paddingRight: normalize(25),
              }}
            >
              <Image
                resizeMode={Image.resizeMode.contain}
                source={{
                  uri: custom_symbol,
                }}
                style={{ height: normalize(25), width: normalize(25) }}
              />
              <Text
                bold
                style={styles.offertext}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {custom_text}
              </Text>
            </View>
          ) : (
            <></>
          )}
        </View>
      </TouchableNativeFeedback>
    );
  },
);

export default Product;

const styles = StyleSheet.create({
  container: {
    width: "100%",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.1)",
    paddingTop: normalize(10),
    paddingBottom: normalize(10),
    paddingRight: normalize(15),
    paddingLeft: normalize(15),
    backgroundColor: "white",
  },
  productTitle: {
    fontSize: normalize(15),
    lineHeight: normalize(18),
    marginBottom: normalize(5),
    height: 40,
  },
  mrp: {
    lineHeight: normalize(16),
    color: "rgba(0, 0, 0, 0.86)",
  },
  price_per_unit: {
    color: "rgba(0, 0, 0, 0.56)",
  },
  offertext: {
    color: "rgba(0, 0, 0, 0.56)",
    fontSize: normalize(13),
    marginLeft: normalize(6),
  },
});
